import React, { useContext, useEffect, useRef, useState } from "react";
import { Map as YMap, useYMaps } from "@pbe/react-yandex-maps";
import { AddressSearchContext } from "../../context/addressSearchContext.js";

const Map = ({ initialCoordinates }) => {
    const ymaps = useYMaps(["Map"]);
    const mapRef = useRef(null);
    const { selectedAddress, onSelectAddress, isUserInteracting, onSetIsUserInteracting } = useContext(AddressSearchContext);
    const [isLoadingLocation, setIsLoadingLocation] = useState(false);
    const [hasInitialized, setHasInitialized] = useState(false);

    const getGeoLocation = async (e) => {
        if (ymaps && isUserInteracting) {
            try {
                const coord = e.get("target").getCenter();
                const response = await ymaps.geocode(coord);
                const currentAddress = response.geoObjects.get(0)?.getAddressLine();

                if (currentAddress) {
                    onSelectAddress({ address: currentAddress, location: coord });
                }
            } catch (error) {
                // Silently handle geocoding errors - they're common and don't break functionality
                // Errors like 'scriptError' from Yandex Maps API are expected in some scenarios
            } finally {
                // Always reset interaction state, even if geocoding fails
                onSetIsUserInteracting(false);
            }
        }
    };

    const handleUseCurrentLocation = async () => {
        if (!ymaps || !mapRef.current) {
            return;
        }

        // Check if geolocation is supported
        if (!("geolocation" in navigator)) {
            return;
        }

        setIsLoadingLocation(true);

        // Get current position
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    if (!position || !position.coords) {
                        setIsLoadingLocation(false);
                        return;
                    }
                    const coord = [position.coords.latitude, position.coords.longitude];
                    
                    // Center map on current location
                    onSetIsUserInteracting(true);
                    await mapRef.current?.setCenter(coord, 17);

                    // Get address for current location
                    if (ymaps && ymaps.geocode) {
                        const response = await ymaps.geocode(coord);
                        if (response && response.geoObjects) {
                            const firstGeoObject = response.geoObjects.get(0);
                            if (firstGeoObject) {
                                const currentAddress = firstGeoObject.getAddressLine();
                                if (currentAddress) {
                                    onSelectAddress({ address: currentAddress, location: coord });
                                }
                            }
                        }
                    }

                    onSetIsUserInteracting(false);
                    setIsLoadingLocation(false);
                } catch (error) {
                    // Silently handle geocoding errors - they're common on mobile
                    if (error instanceof Error && error.message && error.message !== 'scriptError') {
                        console.error("Error geocoding location:", error);
                    }
                    onSetIsUserInteracting(false);
                    setIsLoadingLocation(false);
                }
            },
            (error) => {
                // Only log real geolocation errors (permission denied, timeout, etc.)
                if (error instanceof Error) {
                    console.error("Geolocation error:", error);
                }
                setIsLoadingLocation(false);
            },
            {
                enableHighAccuracy: false,
                timeout: 10000,
                maximumAge: 300000
            }
        );
    };

    useEffect(() => {
        (async () => {
            if (selectedAddress && !isUserInteracting && mapRef.current) {
                try {
                    await mapRef.current.setCenter(selectedAddress.location);
                } catch (error) {
                    // Silently handle map center errors
                } finally {
                    onSetIsUserInteracting(false);
                }
            }
        })();
    }, [selectedAddress, isUserInteracting, onSetIsUserInteracting]);

    // Reset interaction state on window resize (when switching between mobile/desktop)
    useEffect(() => {
        let resizeTimeout;
        const handleResize = () => {
            // Clear any pending resize handling
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            
            // Reset interaction state when viewport changes
            onSetIsUserInteracting(false);
            
            // Invalidate map size to ensure it renders correctly after resize
            resizeTimeout = setTimeout(() => {
                if (mapRef.current) {
                    try {
                        // Force map to recalculate its size
                        mapRef.current.container.fitToViewport();
                    } catch (error) {
                        // Silently handle map resize errors
                    }
                }
            }, 150);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
        };
    }, [onSetIsUserInteracting]);

    const onLoad = () => {
        if (!isUserInteracting && mapRef.current && !hasInitialized) {
            // Priority: selectedAddress > initialCoordinates
            let coordinatesToUse = null;
            
            if (selectedAddress && selectedAddress.location && Array.isArray(selectedAddress.location) && selectedAddress.location.length >= 2) {
                coordinatesToUse = selectedAddress.location;
            } else if (initialCoordinates && Array.isArray(initialCoordinates) && initialCoordinates.length >= 2) {
                coordinatesToUse = initialCoordinates;
            } else if (initialCoordinates && typeof initialCoordinates === 'object' && initialCoordinates.latitude && initialCoordinates.longitude) {
                coordinatesToUse = [initialCoordinates.latitude, initialCoordinates.longitude];
            }
            
            if (coordinatesToUse) {
                onSetIsUserInteracting(true);
                setHasInitialized(true);
                setTimeout(async () => {
                    try {
                        await mapRef.current?.setCenter(coordinatesToUse);
                    } catch (error) {
                        // Silently handle map center errors
                    } finally {
                        onSetIsUserInteracting(false);
                    }
                }, 0);
            }
        }
    };

    return (
        <div className="relative w-full h-[500px] sm:h-[400px] md:h-[500px]">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 pointer-events-none">
                <svg width={30} height={30} viewBox="0 0 24 24" fill="none" stroke="red" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                </svg>
            </div>
            <button
                type="button"
                onClick={handleUseCurrentLocation}
                disabled={isLoadingLocation}
                className="absolute top-4 right-4 z-40 bg-white hover:bg-gray-100 text-customOrange shadow-md p-2 rounded-md disabled:opacity-50"
            >
                {isLoadingLocation ? (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                ) : (
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                )}
            </button>
            <YMap
                onLoad={onLoad}
                instanceRef={(ref) => { mapRef.current = ref; }}
                defaultState={{
                    center: [40.188508, 44.516095],
                    zoom: 17
                }}
                className="w-full h-full"
                onBoundsChange={getGeoLocation}
                modules={["geolocation", "geocode"]}
                onActionBegin={() => { onSetIsUserInteracting(true); }}
            />
        </div>
    );
};

export default Map;

